import { FormikWrapper, FormTextField as TextField } from '@uncinc/uncinc-react-kitchen-sink';
import * as Yup from 'yup';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { type ImmutableRootState } from '@uncinc/react-drupal-core/types';
import { type FormikHelpers } from 'formik';
import { UI_OPERATION_STATUS_REQUEST } from '@uncinc/react-drupal-core';
import Button from '../../Button';
import useClasses from '../../../hooks/useClasses';
import useRoutes from '../../../hooks/useRoutes';
import useOperationStatus from '../../../hooks/useOperationStatus';
import useTypedDispatch from '../../../hooks/useTypedDispatch';
import { userUpdate } from '../../../store/actions/User';
import useToast from '../../../hooks/useToast';
import { getRegistrationData } from '../../../store/actions/Registrations';

import './style.scss';

interface Props {
  onClose?: () => void;
  onNicknameCreated: () => void;
}

interface FormValues {
  nickname: string;
}

const CreateNicknamePopup: React.FC<Props> = ({
  onClose,
  onNicknameCreated,
}) => {
  const { t } = useTranslation();

  const dispatch = useTypedDispatch();

  const toast = useToast();

  const operationStatus = useOperationStatus('user', 'update');

  const { forumRegulations } = useRoutes();

  const { buildClass } = useClasses('CreateNicknamePopup');

  const uid = useSelector<ImmutableRootState<number>, number>((state) => state.getIn(['login', 'uid'], null));

  const validationSchema = Yup.object().shape({
    nickname: Yup.string()
      .trim()
      .required(t('pages.forum_nickname.fields.nickname.validation.required')),
  });

  const handleSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    await dispatch(userUpdate(uid, {
      field_nickname: {
        value: values.nickname,
      },
    }))
      .then(() => {
        toast.show(t('pages.forum_nickname.success_message'), 'success');
        dispatch(getRegistrationData()).catch(() => {});
        onNicknameCreated();
      })
      .catch((err) => {
        // Check if it's a nickname already exists error
        if (err.status === 422 && err.drupalError?.message) {
          // Check if the error message contains field_nickname
          if (err.drupalError.message.includes('field_nickname')) {
            toast.show(t('pages.forum_nickname.error_message_exists'), 'error');
            console.error(err);
            return;
          }
        }

        toast.show(t('pages.forum_nickname.error_message'), 'error');
        console.error(err);
      });
    setSubmitting(false);
  };

  return (
    <div className={buildClass()}>
      <p className="mb-4">
        <Trans i18nKey="pages.forum_nickname.body">
          {/* eslint-disable-next-line max-len */}
          {/* eslint-disable-next-line jsx-a11y/control-has-associated-label, jsx-a11y/anchor-has-content, react/jsx-no-target-blank */}
          <a
            href={forumRegulations}
            target="_blank"
          />
        </Trans>
      </p>
      <div>
        <FormikWrapper
          initialValues={{ nickname: '' }}
          validationSchema={validationSchema}
          validateOnBlur={false}
          validateOnChange={false}
          onSubmit={handleSubmit}
        >
          <TextField
            fieldName="nickname"
            title={t('pages.forum_nickname.fields.nickname.label')}
            placeholder={t('pages.forum_nickname.fields.nickname.placeholder')}
            description={t('pages.forum_nickname.fields.nickname.description')}
            descriptionAboveInput
          />

          <div className={buildClass('footer')}>
            <Button
              htmlType="submit"
              buttonState={operationStatus === UI_OPERATION_STATUS_REQUEST && 'disabled'}
            >
              {t('pages.forum_nickname.fields.submit')}
            </Button>
            {onClose && (
              <button
                type="button"
                className="Popup__close"
                onClick={onClose}
              >
                {t('pages.forum_nickname.cancel')}
              </button>
            )}
          </div>
        </FormikWrapper>
      </div>
    </div>
  );
};

export default CreateNicknamePopup;
